/**
 * The generated handle a new account's personal namespace starts with (I-0657, AC-M1).
 *
 * Jay, 2026-09-24: *"by default, users dont need to be forced to create a custom name. we can also
 * generate a key, that can be changed later"*, and *"the generated key should not be as long as a
 * uuid but something of maybe 10 chars with low collision risk"*.
 *
 * ⛔ **Not derived from the email address.** The previous handle was the address's local part, so
 * `jay.rose@…` published `jay-rose` in every link to every work area that person made. A generated
 * handle carries nothing about the person, and they can change it later (ADR-111).
 *
 * **The alphabet has no look-alikes.** `0`/`o` and `1`/`l`/`i` are left out, so a handle can be read
 * aloud or copied off a screen without guessing. Every symbol is a lowercase letter or digit, so a
 * handle always satisfies the namespace path shape. It starts with a letter, so it never reads as a
 * number.
 *
 * **Collision risk.** `23 · 31⁹ ≈ 6.1·10¹⁴` handles, about 49 bits:
 * - the chance that a **new** account's first draw is already taken, with a million handles in use,
 *   is about `1.6·10⁻⁹`;
 * - the chance that **any** two of a million independent draws coincide is about `8·10⁻⁴` (the
 *   birthday bound).
 *
 * The second figure is why the insert retries on the unique index (AC-M3) instead of trusting the
 * odds. A clash costs one more draw and is never an error.
 */
import { isWellFormedNamespacePath } from './namespaces.js';
import { isReservedNamespaceName } from './addressing/reservedNames.js';

/** Lowercase letters and digits without `0 o 1 l i`: 23 letters and 8 digits. */
export const NAMESPACE_HANDLE_ALPHABET = 'abcdefghjkmnpqrstuvwxyz23456789';

/** The first character is drawn from the letters only. */
const NAMESPACE_HANDLE_FIRST_ALPHABET = 'abcdefghjkmnpqrstuvwxyz';

export const NAMESPACE_HANDLE_LENGTH = 10;

/**
 * A source of uniform random integers in `[0, bound)`. Injected so a test can force a particular
 * draw (a clash, a reserved word) without patching globals.
 */
export type RandomIndexType = (bound: number) => number;

/**
 * Uniform over `[0, bound)` from the platform's cryptographic generator, without modulo bias.
 *
 * `globalThis.crypto.getRandomValues` exists in every runtime this package runs in: browsers,
 * React Native with its polyfill, and Node 19+ (the backend runs 20).
 */
export const cryptoRandomIndex: RandomIndexType = (bound) => {
  const limit = Math.floor(0x1_0000_0000 / bound) * bound;
  const buffer = new Uint32Array(1);
  for (;;) {
    globalThis.crypto.getRandomValues(buffer);
    if (buffer[0] < limit) return buffer[0] % bound;
  }
};

function draw(random: RandomIndexType): string {
  let handle = NAMESPACE_HANDLE_FIRST_ALPHABET[random(NAMESPACE_HANDLE_FIRST_ALPHABET.length)];
  while (handle.length < NAMESPACE_HANDLE_LENGTH) {
    handle += NAMESPACE_HANDLE_ALPHABET[random(NAMESPACE_HANDLE_ALPHABET.length)];
  }
  return handle;
}

/**
 * Whether a string could have come from {@link generateNamespaceHandle}. Tests and the migration
 * spec use it to tell a generated handle from a derived one.
 */
export function isGeneratedNamespaceHandleShape(value: string): boolean {
  return (
    value.length === NAMESPACE_HANDLE_LENGTH &&
    NAMESPACE_HANDLE_FIRST_ALPHABET.includes(value[0]) &&
    [...value].every((character) => NAMESPACE_HANDLE_ALPHABET.includes(character))
  );
}

/**
 * One handle: 10 characters, well-formed, never reserved.
 *
 * ⚠️ A draw that is reserved or malformed is discarded and drawn again. With this alphabet neither
 * can happen, because no reserved name is exactly ten characters from it and every symbol fits the
 * path shape. The check is here so that a change to either list cannot quietly produce a handle
 * the rename form would refuse.
 */
export function generateNamespaceHandle(random: RandomIndexType = cryptoRandomIndex): string {
  for (;;) {
    const handle = draw(random);
    if (isWellFormedNamespacePath(handle) && !isReservedNamespaceName(handle)) return handle;
  }
}
